import React from 'react';
/**
 * Calls an async function, and returns an object that can be used in a component to render loading indicator or final
 * result or an error message. Note that function and arguments are used as dependencies in the useEffect hook that's
 * used internally. This means that any changes to function or it's arguments will trigger the useEffect and the
 * function will be called.
 *
 * WARNING: Do not pass new objects to args as this will cause and infinite loop.
 * ```
 * // BAD CODE
 * usePromiseResult(
 *    (param: number) => functionToCall(param), // creates a new function every time, will result in infinite loop
 *    123,
 * );

 * // BAD CODE
 * usePromiseResult(
 *    functionToCall,
 *    [1, 2, 3], // creates a new array every time, will result in infinite loop
 * );
 *
 * // GOOD CODE
 * const param = React.useMemo(() => ([1, 2, 3]), []);
 * usePromiseResult(
 *    functionToCall,
 *    param,
 * );
 * ```
 *
 * @param functionToCall - async function to call
 * @param args - arguments to pass to the function
 */
export function usePromiseResult(functionToCall, ...args) {
    const [state, setState] = React.useState({ loading: true });
    React.useEffect(() => {
        if (functionToCall) {
            let ignore = false;
            setState((prevState) => (Object.assign(Object.assign({}, prevState), { loading: true })));
            functionToCall(...args)
                .then((result) => {
                if (!ignore) {
                    setState({ result });
                }
            })
                .catch((error) => {
                if (!ignore) {
                    setState({ error });
                }
            });
            return () => {
                ignore = true;
            };
        }
        else {
            setState({ result: undefined });
            return undefined;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [functionToCall, ...args]);
    return state;
}
